/**
 * Бейджи состояния живой карточки ЭДО: детальный статус документа (EdoStatusBadge, спец. §4.2)
 * и статус выплаты отпускных (AccrualStatus, ТЗ-2026-004 шаг 11).
 */
import { Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Icon } from '../../../ui'
import type { EdoStatus, RequestCard } from '../../../lib/types'

/** Granular EDO status → label + tone (spec §4.2). Self-contained for i18n. */
export const EDO_STATUS: Record<EdoStatus, { label: Localized; tone: string }> = {
  // Увольнение (ТЗ-2026-008): «Завершён» по скану подписанного приказа; проведение в 1С — позже.
  AWAITING_TERMINATION_POSTING: { label: { ru: 'Завершён · ожидает проведения в 1С', en: 'Completed · awaiting posting in 1C', az: 'Tamamlandı · 1C-də keçirilməni gözləyir' }, tone: 'green' },
  AWAITING_HIRE_POSTING: { label: { ru: 'Черновик создан, ожидает проведения', en: 'Draft created, awaiting posting' }, tone: 'gold' },
  DRAFT: { label: { ru: 'Черновик', en: 'Draft' }, tone: 'default' },
  MGR_REVIEW: { label: { ru: 'На согласовании (руководитель)', en: 'Manager review' }, tone: 'blue' },
  SUP_REVIEW: { label: { ru: 'На согласовании (рук. СП)', en: 'Supervisor review' }, tone: 'blue' },
  HR_REVIEW: { label: { ru: 'На контроле HR', en: 'HR review' }, tone: 'blue' },
  SIGNING: { label: { ru: 'На подписи', en: 'Signing' }, tone: 'blue' },
  APPROVED: { label: { ru: 'Одобрено', en: 'Approved' }, tone: 'green' },
  POSTED: { label: { ru: 'Проведено в 1С', en: 'Posted to 1C' }, tone: 'green' },
  NOT_REVIEWED: { label: { ru: 'Не рассмотрено в срок', en: 'Not reviewed in time' }, tone: 'gold' },
  REWORK: { label: { ru: 'На доработке', en: 'Rework' }, tone: 'gold' },
  REJECTED: { label: { ru: 'Отклонено / отозвано', en: 'Rejected / withdrawn' }, tone: 'red' },
  POST_FAILED: { label: { ru: 'Ошибка проводки', en: 'Posting failed' }, tone: 'red' },
  AWAITING_ACCRUAL: { label: { ru: 'Начисление (бухгалтер)', en: 'Accrual (accountant)' }, tone: 'blue' },
  // Командировка v1.2: оформление ведёт кадровик юрлица (HR); приказ создаёт 1С, кадровик вкладывает скан.
  TRAVEL_BOOKING: { label: { ru: 'Оформление референтом', en: 'Referent booking' }, tone: 'blue' },
  EXPENSE_REVIEW: { label: { ru: 'Расчёт бухгалтера', en: 'Accountant calculation' }, tone: 'blue' },
  ORDER_CREATION: { label: { ru: 'Приказ: подпись и скан', en: 'Order: signature & scan' }, tone: 'blue' },
  CFO_APPROVAL: { label: { ru: 'Согласование начисления (CFO)', en: 'Accrual approval (CFO)' }, tone: 'blue' },
  // Подбор персонала (ТЗ-2026-011)
  CEO_REVIEW: { label: { ru: 'На согласовании (ГД)', en: 'CEO review' }, tone: 'blue' },
  IN_PROGRESS: { label: { ru: 'В работе (старт поиска)', en: 'In progress' }, tone: 'gold' },
  SEARCHING: { label: { ru: 'Поиск ведётся', en: 'Search in progress' }, tone: 'gold' },
  CLOSED: { label: { ru: 'Закрыта (кандидат найден)', en: 'Closed (candidate hired)' }, tone: 'green' },
  CANCELLED: { label: { ru: 'Отменена', en: 'Cancelled' }, tone: 'default' },
  // Больничный лист (волна 07.2026): REGISTERED — терминальный успех (приказ создан в 1С).
  REGISTERED: { label: { ru: 'Зарегистрирован', en: 'Registered' }, tone: 'green' },
  DSMF_NOTIFYING: { label: { ru: 'Уведомление в DSMF', en: 'DSMF notification' }, tone: 'gold' },
  // Произвольный документ: у очередного согласующего из маршрута инициатора.
  IN_APPROVAL: { label: { ru: 'На согласовании', en: 'In approval' }, tone: 'blue' },
  // Договор: маршрут пройден, автор прикладывает подписанный экземпляр (23.09.2026).
  AWAITING_SIGNED_COPY: {
    label: { ru: 'Ожидает подписанный экземпляр', en: 'Awaiting signed copy', az: 'İmzalanmış nüsxə gözlənilir' },
    tone: 'gold',
  },
  // Договор: подписанный экземпляр отправлен, идёт проводка в 1С (23.09.2026).
  POSTING: {
    label: { ru: 'Передаётся в 1С', en: 'Sending to 1C', az: '1C-yə göndərilir' },
    tone: 'blue',
  },
  EXIT_INTERVIEW: { label: { ru: 'Exit-интервью (HR)', en: 'Exit interview (HR)', az: 'Exit-müsahibə (HR)' }, tone: 'blue' },
}

export function EdoStatusBadge({ status }: { status: EdoStatus }) {
  const { t } = useApp()
  const meta = EDO_STATUS[status] ?? { label: { ru: status, en: status }, tone: 'default' }
  const tn = tone(meta.tone)
  return (
    <XStack backgroundColor={tn.soft} borderRadius={999} paddingHorizontal={11} paddingVertical={5} alignSelf="flex-start">
      <Text fontSize={11} fontWeight="700" color={tn.text} numberOfLines={1}>
        {t(meta.label)}
      </Text>
    </XStack>
  )
}

/** Vacation-pay accrual status badge (ТЗ-2026-004 шаг 11) — Paid/Pending; null otherwise. */
export function AccrualStatus({ detail }: { detail?: RequestCard }) {
  const { t } = useApp()
  const payStatus = detail?.type === 'VACATION' && detail.status === 'POSTED' ? detail.result?.paymentStatus : undefined
  if (!payStatus) return null
  const accrued = payStatus === 'Paid'
  return (
    <XStack
      alignItems="center"
      gap={10}
      padding={12}
      borderRadius={7}
      borderWidth={1}
      backgroundColor={accrued ? tone('green').soft : tone('gold').soft}
      borderColor={accrued ? tone('green').solid : tone('gold').solid}
    >
      <Icon name="wallet" size={16} color={accrued ? tone('green').text : tone('gold').text} />
      <YStack flex={1}>
        <Text fontSize={13} fontWeight="700" color={accrued ? tone('green').text : tone('gold').text}>
          {accrued
            ? t({ ru: 'Отпускные начислены', en: 'Vacation pay accrued' })
            : t({ ru: 'Ожидает начисления бухгалтером', en: 'Awaiting accrual by accountant' })}
        </Text>
        {accrued && detail?.result?.vacationPay != null && (
          <Text fontSize={11} color="$muted">
            {detail.result.vacationPay} · {t({ ru: 'сумму подтверждает 1С', en: 'amount confirmed by 1C' })}
          </Text>
        )}
      </YStack>
    </XStack>
  )
}
